import { useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { __ } from '@wordpress/i18n'
import Button from '../Common/Button';
import SbUtils from '../../Utils/SbUtils';
import Input from '../Common/Input';
import Notice from '../Global/Notice';
import Checkbox from '../Common/Checkbox';
import WooProductSelector from '../Common/WooProductSelector';
import EddDownloadSelector from '../Common/EddDownloadSelector';
import useWooValidation from '../../hooks/useWooValidation';
import useEddValidation from '../../hooks/useEddValidation';

const AddSourceModal = ( props ) => {
    const currentContext =  SbUtils.getCurrentContext();
    const {
        sbCustomizer,
        editorTopLoader,
        editorNotification,
        sourcesCount,
        apis,
        fbModal,
        apiLimits,
        globalNotices,
        allowedTiers,
        isPro,
        upsellModal,
        bulkModal,
        freeRet,
        addSModal,
        freeRetModal
    } = useContext( currentContext );
    const [ selectedProvider, setSelectedProvider ] = useState( null );

    const [ currentStep, setCurrentStep ] = useState( 'selectProvider' );
    const [ apiKey, setApiKey ] = useState( '' );
    const [ providerIdUrl, setProviderIdUrl ] = useState( '' );
    const [ inputError, setInputError ] = useState( null ); //Will serve in case of adding Invalid API key or Wrong Place/Location URL/ID

    const [ selectedFBPages , setSelectedFBPages ] = useState( [ ] );

    // WooCommerce-specific state
    const [ wooProducts, setWooProducts ] = useState( [] );
    const [ loadingProducts, setLoadingProducts ] = useState( false );
    const [ isSearchingProducts, setIsSearchingProducts ] = useState( false );
    const [ wooSearchQuery, setWooSearchQuery ] = useState( '' );

    // WooCommerce multi-product selection state
    const [ selectedProducts, setSelectedProducts ] = useState( [] ); // Array of selected product objects
    const [ sourceName, setSourceName ] = useState( '' ); // User-defined source name
    const [ wooValidationError, setWooValidationError ] = useState( null ); // Validation error message

    // WooCommerce categories and tags state (for multi-tab selection)
    const [ wooCategories, setWooCategories ] = useState( [] );
    const [ wooTags, setWooTags ] = useState( [] );
    const [ selectedCategories, setSelectedCategories ] = useState( [] );
    const [ selectedTags, setSelectedTags ] = useState( [] );
    const [ loadingCategories, setLoadingCategories ] = useState( false );
    const [ loadingTags, setLoadingTags ] = useState( false );

    // WooCommerce validation hook (handles zero reviews warning automatically)
    const { hasAnySelection: hasAnyWooSelection, validateSelection: validateWooSelection } = useWooValidation( {
        selectedProducts,
        selectedCategories,
        selectedTags,
        setValidationError: setWooValidationError,
        enabled: selectedProvider === 'woocommerce' && currentStep === 'addProducts'
    } );

    // EDD (Easy Digital Downloads) specific state
    const [ eddDownloads, setEddDownloads ] = useState( [] );
    const [ loadingDownloads, setLoadingDownloads ] = useState( false );
    const [ isSearchingDownloads, setIsSearchingDownloads ] = useState( false );

    // EDD multi-download selection state
    const [ selectedDownloads, setSelectedDownloads ] = useState( [] ); // Array of selected download objects
    const [ eddSourceName, setEddSourceName ] = useState( '' ); // User-defined source name
    const [ eddValidationError, setEddValidationError ] = useState( null ); // Validation error message

    // EDD categories and tags state (for multi-tab selection)
    const [ eddCategories, setEddCategories ] = useState( [] );
    const [ eddTags, setEddTags ] = useState( [] );
    const [ selectedEddCategories, setSelectedEddCategories ] = useState( [] );
    const [ selectedEddTags, setSelectedEddTags ] = useState( [] );
    const [ loadingEddCategories, setLoadingEddCategories ] = useState( false );
    const [ loadingEddTags, setLoadingEddTags ] = useState( false );

    // EDD validation hook (handles zero reviews warning automatically)
    const { hasAnySelection: hasAnyEddSelection, validateSelection: validateEddSelection } = useEddValidation( {
        selectedDownloads,
        selectedCategories: selectedEddCategories,
        selectedTags: selectedEddTags,
        setValidationError: setEddValidationError,
        enabled: selectedProvider === 'edd' && currentStep === 'addDownloads'
    } );

    const defaultApikeyNotice = {
        type: 'default',
        text : __( 'You can also skip this step, but you will need to add an API key later to keep the reviews updated.', 'sb-customizer' )
    };
    const [ apiKeyNoticeInfo, setApiKeyNoticeInfo ] = useState( defaultApikeyNotice );

    const apiKeys  = apis.apiKeys;
    const providers = isPro ? sbCustomizer.providers : sbCustomizer.providers.filter( pr => allowedTiers.tiers.includes( pr.type ) );
    const advancedProviders = isPro ? [] : sbCustomizer.providers.filter( pr => !allowedTiers.tiers.includes( pr.type ) );
    const providersWithNoApiKey = sbCustomizer.providers.map( pr => pr.onlyDetails === true && pr.type ).filter(Boolean);;
    const providersNeedApiKey = sbCustomizer.providers.map( pr => ( pr?.apiKey && pr?.apiKey === true ) && pr.type ).filter(Boolean);
    const mandatoryApiKeyProviders = sbCustomizer.providers.map( pr => ( pr?.mandatoryApiKey && pr?.mandatoryApiKey === true ) && pr.type ).filter(Boolean);;
    const needsApiKey = providersNeedApiKey.includes( selectedProvider ) || mandatoryApiKeyProviders.includes( selectedProvider );
    const selectedProviderInfo = providers.filter( provider =>  provider.type === selectedProvider)[0];
    const [ providerUrlIdNotice, setProviderUrlIdNotice ] = useState( null );

    const [ tiersInfo, setTiersInfo ] = useState(sbCustomizer?.pluginStatus?.tiers_info);


    const setAPIKeyNotice = ( type = 'default') => {
        let noticeMessage = defaultApikeyNotice;
        if(type === 'sourceLimitExceeded'){
            noticeMessage = {
                type: 'error',
                text : __( 'You have reached the maximum sources limit, Please enter a valid API key to retrieve new sources.', 'sb-customizer' ) + '<br/>' + __( 'Are you using a local host? API keys may be required for all review retrieving.', 'sb-customizer' )
            };
        }
        setApiKeyNoticeInfo( noticeMessage );
    }



    const selectFBPages = ( pageID ) => {
        let fbPages = [...selectedFBPages];
        if( ! fbPages.includes( pageID ) ){
            fbPages.push( pageID )
        }else{
            fbPages.splice( fbPages.indexOf( pageID ), 1 );
        }
        setSelectedFBPages( [...fbPages] )
    }

    const getSelectedPagesInfo = (  ) => {
        return selectedFBPages.map( pageID => {
            let page = SbUtils.findElementById( sbCustomizer.newSourceData.pages , 'id', pageID );
            return {
                id : page.id,
                name : page.name,
                rating : page?.overall_star_rating ? page?.overall_star_rating : 0,
                total_rating : page?.total_rating ? page?.total_rating : 0,
                provider : 'facebook',
                access_token : page.access_token,
                url : 'https://www.facebook.com/' + page.id,
            }
        });
    }

    const addFacebookPagesSource = () => {
        if( selectedFBPages.length > 0 ){
            const formData = {
                action                  : 'sbr_feed_saver_manager_add_facebook_souce',
                selectedFacebookPages   : JSON.stringify( getSelectedPagesInfo() )
            },
            notificationsContent = {
                success : {
                    icon : 'success',
                    text : __('Facebook Sources Added', 'sb-customizer')
                }
            };
            SbUtils.ajaxPost(
                sbCustomizer.ajaxHandler,
                formData,
                ( data ) => {
                    // Don't update sources list from response - parent will refresh for current page
                    // This prevents showing page 1 results while staying on current pagination page
                    if( data?.sourcesCount !== undefined ){
                        sourcesCount.setSourcesNumber( data?.sourcesCount );
                    }
                    // Notify parent that sources were successfully added
                    if ( props.onSuccess ) {
                        props.onSuccess();
                    }
                    cancelClick()
                },
                editorTopLoader,
                editorNotification,
                notificationsContent
            )
        }
    }

    const loadWooCommerceProducts = useCallback( ( searchTerm = '' ) => {
        // Show skeleton loaders for both initial load and search
        setLoadingProducts( true );
        setIsSearchingProducts( true );
        const startTime = Date.now();
        const minDisplayTime = 500; // Minimum time to show skeleton loaders (ms)

        const formData = {
            action: 'sbr_get_woocommerce_products',
            search: searchTerm,
            nonce: sbCustomizer?.nonce
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( data ) => {
                // Ensure skeleton shows for at least minDisplayTime
                const elapsed = Date.now() - startTime;
                const remainingTime = Math.max( 0, minDisplayTime - elapsed );

                setTimeout( () => {
                    setLoadingProducts( false );
                    setIsSearchingProducts( false );
                    if ( data?.success && data?.data?.products !== undefined ) {
                        setWooProducts( data.data.products );
                    } else {
                        setWooProducts( [] );
                    }
                }, remainingTime );
            },
            null,
            null,
            null
        );
    }, [ sbCustomizer.ajaxHandler ] );

    const loadWooCommerceCategories = () => {
        setLoadingCategories( true );
        const startTime = Date.now();
        const minDisplayTime = 500;

        const formData = {
            action: 'sbr_get_woocommerce_categories',
            nonce: sbCustomizer?.nonce
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( data ) => {
                const elapsed = Date.now() - startTime;
                const remainingTime = Math.max( 0, minDisplayTime - elapsed );

                setTimeout( () => {
                    setLoadingCategories( false );
                    if ( data?.success && data?.data?.categories !== undefined ) {
                        setWooCategories( data.data.categories );
                    } else {
                        setWooCategories( [] );
                    }
                }, remainingTime );
            },
            null,
            null,
            null
        );
    }

    const loadWooCommerceTags = () => {
        setLoadingTags( true );
        const startTime = Date.now();
        const minDisplayTime = 500;

        const formData = {
            action: 'sbr_get_woocommerce_tags',
            nonce: sbCustomizer?.nonce
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( data ) => {
                const elapsed = Date.now() - startTime;
                const remainingTime = Math.max( 0, minDisplayTime - elapsed );

                setTimeout( () => {
                    setLoadingTags( false );
                    if ( data?.success && data?.data?.tags !== undefined ) {
                        setWooTags( data.data.tags );
                    } else {
                        setWooTags( [] );
                    }
                }, remainingTime );
            },
            null,
            null,
            null
        );
    }

    // WooCommerce multi-product selection helpers
    const toggleProductSelection = ( product ) => {
        setWooValidationError( null ); // Clear validation error when selecting
        const isSelected = selectedProducts.some( p => p.id === product.id );
        if ( isSelected ) {
            setSelectedProducts( selectedProducts.filter( p => p.id !== product.id ) );
        } else {
            setSelectedProducts( [ ...selectedProducts, product ] );
        }
    };

    const selectAllProducts = ( filteredProducts ) => {
        setWooValidationError( null );
        // Merge: add filtered products to existing selection (avoid duplicates)
        const existingIds = new Set( selectedProducts.map( p => p.id ) );
        const newProducts = filteredProducts.filter( p => !existingIds.has( p.id ) );
        setSelectedProducts( [ ...selectedProducts, ...newProducts ] );
    };

    const deselectAllProducts = ( filteredProducts ) => {
        // Only deselect filtered/visible products, preserve non-visible selections
        const filteredIds = new Set( filteredProducts.map( p => p.id ) );
        setSelectedProducts( selectedProducts.filter( p => !filteredIds.has( p.id ) ) );
    };

    // WooCommerce category selection helpers
    const toggleCategorySelection = ( category ) => {
        setWooValidationError( null );
        const isSelected = selectedCategories.some( c => c.id === category.id );
        if ( isSelected ) {
            setSelectedCategories( selectedCategories.filter( c => c.id !== category.id ) );
        } else {
            setSelectedCategories( [ ...selectedCategories, category ] );
        }
    };

    // WooCommerce tag selection helpers
    const toggleTagSelection = ( tag ) => {
        setWooValidationError( null );
        const isSelected = selectedTags.some( t => t.id === tag.id );
        if ( isSelected ) {
            setSelectedTags( selectedTags.filter( t => t.id !== tag.id ) );
        } else {
            setSelectedTags( [ ...selectedTags, tag ] );
        }
    };

    // Batch select/deselect all categories
    const selectAllCategories = ( filteredCategories ) => {
        setWooValidationError( null );
        // Merge: add filtered categories to existing selection (avoid duplicates)
        const existingIds = new Set( selectedCategories.map( c => c.id ) );
        const newCategories = filteredCategories.filter( c => !existingIds.has( c.id ) );
        setSelectedCategories( [ ...selectedCategories, ...newCategories ] );
    };

    const deselectAllCategories = ( filteredCategories ) => {
        // Only deselect filtered/visible categories, preserve non-visible selections
        const filteredIds = new Set( filteredCategories.map( c => c.id ) );
        setSelectedCategories( selectedCategories.filter( c => !filteredIds.has( c.id ) ) );
    };

    // Batch select/deselect all tags
    const selectAllTags = ( filteredTags ) => {
        setWooValidationError( null );
        // Merge: add filtered tags to existing selection (avoid duplicates)
        const existingIds = new Set( selectedTags.map( t => t.id ) );
        const newTags = filteredTags.filter( t => !existingIds.has( t.id ) );
        setSelectedTags( [ ...selectedTags, ...newTags ] );
    };

    const deselectAllTags = ( filteredTags ) => {
        // Only deselect filtered/visible tags, preserve non-visible selections
        const filteredIds = new Set( filteredTags.map( t => t.id ) );
        setSelectedTags( selectedTags.filter( t => !filteredIds.has( t.id ) ) );
    };

    // Clear all selections (products, categories, and tags)
    const clearAllSelections = () => {
        setSelectedProducts( [] );
        setSelectedCategories( [] );
        setSelectedTags( [] );
    };

    const isProductSelected = ( productId ) => {
        return selectedProducts.some( p => p.id === productId );
    };

    // ========================================
    // EDD (Easy Digital Downloads) Functions
    // ========================================

    const loadEddDownloads = useCallback( ( searchTerm = '' ) => {
        // Show skeleton loaders for both initial load and search
        setLoadingDownloads( true );
        setIsSearchingDownloads( true );
        const startTime = Date.now();
        const minDisplayTime = 500; // Minimum time to show skeleton loaders (ms)

        const formData = {
            action: 'sbr_get_edd_downloads',
            search: searchTerm,
            nonce: sbCustomizer?.nonce
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( data ) => {
                // Ensure skeleton shows for at least minDisplayTime
                const elapsed = Date.now() - startTime;
                const remainingTime = Math.max( 0, minDisplayTime - elapsed );

                setTimeout( () => {
                    setLoadingDownloads( false );
                    setIsSearchingDownloads( false );
                    if ( data?.success && data?.data?.downloads !== undefined ) {
                        setEddDownloads( data.data.downloads );
                    } else {
                        setEddDownloads( [] );
                    }
                }, remainingTime );
            },
            null,
            null,
            null
        );
    }, [ sbCustomizer.ajaxHandler ] );

    const loadEddCategories = () => {
        setLoadingEddCategories( true );
        const startTime = Date.now();
        const minDisplayTime = 500;

        const formData = {
            action: 'sbr_get_edd_categories',
            nonce: sbCustomizer?.nonce
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( data ) => {
                const elapsed = Date.now() - startTime;
                const remainingTime = Math.max( 0, minDisplayTime - elapsed );

                setTimeout( () => {
                    setLoadingEddCategories( false );
                    if ( data?.success && data?.data?.categories !== undefined ) {
                        setEddCategories( data.data.categories );
                    } else {
                        setEddCategories( [] );
                    }
                }, remainingTime );
            },
            null,
            null,
            null
        );
    }

    const loadEddTags = () => {
        setLoadingEddTags( true );
        const startTime = Date.now();
        const minDisplayTime = 500;

        const formData = {
            action: 'sbr_get_edd_tags',
            nonce: sbCustomizer?.nonce
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( data ) => {
                const elapsed = Date.now() - startTime;
                const remainingTime = Math.max( 0, minDisplayTime - elapsed );

                setTimeout( () => {
                    setLoadingEddTags( false );
                    if ( data?.success && data?.data?.tags !== undefined ) {
                        setEddTags( data.data.tags );
                    } else {
                        setEddTags( [] );
                    }
                }, remainingTime );
            },
            null,
            null,
            null
        );
    }

    // EDD multi-download selection helpers
    const toggleDownloadSelection = ( download ) => {
        setEddValidationError( null ); // Clear validation error when selecting
        const isSelected = selectedDownloads.some( d => d.id === download.id );
        if ( isSelected ) {
            setSelectedDownloads( selectedDownloads.filter( d => d.id !== download.id ) );
        } else {
            setSelectedDownloads( [ ...selectedDownloads, download ] );
        }
    };

    const selectAllDownloads = ( filteredDownloads ) => {
        setEddValidationError( null );
        // Merge: add filtered downloads to existing selection (avoid duplicates)
        const existingIds = new Set( selectedDownloads.map( d => d.id ) );
        const newDownloads = filteredDownloads.filter( d => !existingIds.has( d.id ) );
        setSelectedDownloads( [ ...selectedDownloads, ...newDownloads ] );
    };

    const deselectAllDownloads = ( filteredDownloads ) => {
        // Only deselect filtered/visible downloads, preserve non-visible selections
        const filteredIds = new Set( filteredDownloads.map( d => d.id ) );
        setSelectedDownloads( selectedDownloads.filter( d => !filteredIds.has( d.id ) ) );
    };

    // EDD category selection helpers
    const toggleEddCategorySelection = ( category ) => {
        setEddValidationError( null );
        const isSelected = selectedEddCategories.some( c => c.id === category.id );
        if ( isSelected ) {
            setSelectedEddCategories( selectedEddCategories.filter( c => c.id !== category.id ) );
        } else {
            setSelectedEddCategories( [ ...selectedEddCategories, category ] );
        }
    };

    // EDD tag selection helpers
    const toggleEddTagSelection = ( tag ) => {
        setEddValidationError( null );
        const isSelected = selectedEddTags.some( t => t.id === tag.id );
        if ( isSelected ) {
            setSelectedEddTags( selectedEddTags.filter( t => t.id !== tag.id ) );
        } else {
            setSelectedEddTags( [ ...selectedEddTags, tag ] );
        }
    };

    // Batch select/deselect all EDD categories
    const selectAllEddCategories = ( filteredCategories ) => {
        setEddValidationError( null );
        const existingIds = new Set( selectedEddCategories.map( c => c.id ) );
        const newCategories = filteredCategories.filter( c => !existingIds.has( c.id ) );
        setSelectedEddCategories( [ ...selectedEddCategories, ...newCategories ] );
    };

    const deselectAllEddCategories = ( filteredCategories ) => {
        const filteredIds = new Set( filteredCategories.map( c => c.id ) );
        setSelectedEddCategories( selectedEddCategories.filter( c => !filteredIds.has( c.id ) ) );
    };

    // Batch select/deselect all EDD tags
    const selectAllEddTags = ( filteredTags ) => {
        setEddValidationError( null );
        const existingIds = new Set( selectedEddTags.map( t => t.id ) );
        const newTags = filteredTags.filter( t => !existingIds.has( t.id ) );
        setSelectedEddTags( [ ...selectedEddTags, ...newTags ] );
    };

    const deselectAllEddTags = ( filteredTags ) => {
        const filteredIds = new Set( filteredTags.map( t => t.id ) );
        setSelectedEddTags( selectedEddTags.filter( t => !filteredIds.has( t.id ) ) );
    };

    // Clear all EDD selections (downloads, categories, and tags)
    const clearAllEddSelections = () => {
        setSelectedDownloads( [] );
        setSelectedEddCategories( [] );
        setSelectedEddTags( [] );
    };

    // Generate a smart default EDD source name based on selected items
    const generateEddSourceName = () => {
        const catCount = selectedEddCategories.length;
        const tagCount = selectedEddTags.length;
        const dlCount = selectedDownloads.length;

        // 1. Single category or tag only → name it after that
        if ( catCount === 1 && tagCount === 0 && dlCount === 0 ) {
            return selectedEddCategories[ 0 ].name;
        }
        if ( tagCount === 1 && catCount === 0 && dlCount === 0 ) {
            return selectedEddTags[ 0 ].name;
        }

        // 2. Category or tag + other items → "[category/tag] (and more)"
        if ( catCount > 0 && ( tagCount > 0 || dlCount > 0 || catCount > 1 ) ) {
            return `${ selectedEddCategories[ 0 ].name } (and more)`;
        }
        if ( tagCount > 0 && ( dlCount > 0 || tagCount > 1 ) ) {
            return `${ selectedEddTags[ 0 ].name } (and more)`;
        }

        // 3. Single download only → name it after that download
        if ( dlCount === 1 && catCount === 0 && tagCount === 0 ) {
            return selectedDownloads[ 0 ].name;
        }

        // 4. Multiple downloads (no category or tag) → "[first download] (and more)"
        if ( dlCount > 1 && catCount === 0 && tagCount === 0 ) {
            return `${ selectedDownloads[ 0 ].name } (and more)`;
        }

        return '';
    };

    // Generate a smart default source name based on selected items
    const generateSourceName = () => {
        const catCount = selectedCategories.length;
        const tagCount = selectedTags.length;
        const prodCount = selectedProducts.length;

        // 1. Single category or tag only → name it after that
        if ( catCount === 1 && tagCount === 0 && prodCount === 0 ) {
            return selectedCategories[ 0 ].name;
        }
        if ( tagCount === 1 && catCount === 0 && prodCount === 0 ) {
            return selectedTags[ 0 ].name;
        }

        // 2. Category or tag + other items → "[category/tag] (and more)"
        if ( catCount > 0 && ( tagCount > 0 || prodCount > 0 || catCount > 1 ) ) {
            return `${ selectedCategories[ 0 ].name } (and more)`;
        }
        if ( tagCount > 0 && ( prodCount > 0 || tagCount > 1 ) ) {
            return `${ selectedTags[ 0 ].name } (and more)`;
        }

        // 3. Single product only → name it after that product
        if ( prodCount === 1 && catCount === 0 && tagCount === 0 ) {
            return selectedProducts[ 0 ].name;
        }

        // 4. Multiple products (no category or tag) → "[first product] (and more)"
        if ( prodCount > 1 && catCount === 0 && tagCount === 0 ) {
            return `${ selectedProducts[ 0 ].name } (and more)`;
        }

        return '';
    };

    // Load WooCommerce products, categories, and tags when provider is selected
    useEffect(() => {
        if ( selectedProvider === 'woocommerce' && ( currentStep === 'addProducts' ) ) {
            loadWooCommerceProducts();
            loadWooCommerceCategories();
            loadWooCommerceTags();
        }
    }, [ selectedProvider, currentStep ]);

    // Load EDD downloads, categories, and tags when provider is selected
    useEffect(() => {
        if ( selectedProvider === 'edd' && ( currentStep === 'addDownloads' ) ) {
            loadEddDownloads();
            loadEddCategories();
            loadEddTags();
        }
    }, [ selectedProvider, currentStep ]);

    // Auto-populate source name when entering the addSourceName step
    useEffect(() => {
        if ( selectedProvider === 'woocommerce' && currentStep === 'addSourceName' ) {
            const autoName = generateSourceName();
            if ( autoName ) {
                setSourceName( autoName );
            }
        }
        if ( selectedProvider === 'edd' && currentStep === 'addEddSourceName' ) {
            const autoName = generateEddSourceName();
            if ( autoName ) {
                setEddSourceName( autoName );
            }
        }
    }, [ currentStep, selectedProvider ]);

    const connectFacebookActiveMemo = useMemo( () => {
        if( fbModal.connectFacebookActive === true ){
            setCurrentStep('addSourceDetails')
            setSelectedProvider( 'facebook' )
            return true;
        }
        return false;
    },[ fbModal ]);

    const addSourcesSteps = [
        {
            id : 'selectProvider',
            heading : __( 'Select Source Type', 'sb-customizer' )
        },
        {
            id : 'addApiKey',
            heading : __( 'Add API Key', 'sb-customizer' ),
            condition : needsApiKey && apiKeys[selectedProvider] === undefined && selectedProvider !== 'woocommerce' && selectedProvider !== 'edd'
        },
        {
            id : 'addProducts',
            heading : __( 'Add Products', 'sb-customizer' ),
            condition : selectedProvider === 'woocommerce'
        },
        {
            id : 'addSourceName',
            heading : __( 'Add Source Name', 'sb-customizer' ),
            condition : selectedProvider === 'woocommerce'
        },
        {
            id : 'addDownloads',
            heading : __( 'Add Downloads', 'sb-customizer' ),
            condition : selectedProvider === 'edd'
        },
        {
            id : 'addEddSourceName',
            heading : __( 'Add Source Name', 'sb-customizer' ),
            condition : selectedProvider === 'edd'
        },
        {
            id : 'addSourceDetails',
            heading : __( 'Add Source Details', 'sb-customizer' ),
            condition : selectedProvider !== 'woocommerce' && selectedProvider !== 'edd'
        }
    ];

    const navTopClick = ( stepID ) => {
        if( stepID === 'addApiKey' &&  selectedProvider !== undefined){
            setCurrentStep('addApiKey')
        }
        if( stepID === 'selectProvider'){
            setCurrentStep('selectProvider')
            // Clear URL input when navigating back to provider selection
            setProviderIdUrl( '' );
            setInputError( null );
        }
    }

    const providerUrlIdNoticeList = {
        'wordpress.org' : __('Your URL must contain "/themes/" or "/plugins/". Examples: <a href="#">https://wordpress.org/plugins/instagram-feed/</a>, <a href="#">https://wordpress.org/themes/twentytwentythree/</a>', 'sb-customizer'),
        'airbnb' : __('Enter an Airbnb listing URL or numeric ID. Example: <a href="#">https://www.airbnb.com/rooms/12345678</a>', 'sb-customizer'),
        'booking' : __('Enter a Booking.com hotel URL or numeric ID. Example: <a href="#">https://www.booking.com/hotel/us/hotel-name.html</a>', 'sb-customizer'),
        'aliexpress' : __('Enter an AliExpress product URL or numeric ID. Example: <a href="#">https://www.aliexpress.com/item/1234567890123.html</a>', 'sb-customizer')
    }

    const checkValidIDUrl = () => {
        const input = providerIdUrl.trim();
        switch (selectedProvider) {
            case 'wordpress.org':
                return input.includes('plugins/') || input.includes('themes/')
            case 'airbnb':
                // Accept numeric ID (>5 digits) or airbnb.com/rooms/ URL
                if (/^\d{6,}$/.test(input)) return true;
                return /(?:www\.)?airbnb\.com\/rooms\/\d+/i.test(input);
            case 'booking':
                // Accept numeric ID or booking.com/hotel/ URL
                if (/^\d+$/.test(input)) return true;
                return /booking\.com\/hotel\//i.test(input);
            case 'aliexpress':
                // Accept numeric ID (>10 digits) or aliexpress.com/item/ URL
                if (/^\d{10,}$/.test(input)) return true;
                return /aliexpress\.com\/item\/\d+/i.test(input);
            default:
                return true;
        }
    }

    const finishStepClick = () => {
        // WooCommerce multi-product validation
        if ( selectedProvider === 'woocommerce' ) {
            const validationMsg = validateWooSelection();
            if ( validationMsg ) {
                setWooValidationError( validationMsg );
                setCurrentStep( 'addProducts' ); // Go back to add products step
                return false;
            }
            setWooValidationError( null );
        }

        // EDD multi-download validation
        if ( selectedProvider === 'edd' ) {
            const validationMsg = validateEddSelection();
            if ( validationMsg ) {
                setEddValidationError( validationMsg );
                setCurrentStep( 'addDownloads' ); // Go back to add downloads step
                return false;
            }
            setEddValidationError( null );
        }

        // For WooCommerce/EDD with multi-selection, we don't need providerIdUrl check
        const needsProviderIdUrl = !(
            ( selectedProvider === 'woocommerce' && hasAnyWooSelection() ) ||
            ( selectedProvider === 'edd' && hasAnyEddSelection() )
        );

        if( needsProviderIdUrl && SbUtils.checkNotEmpty( providerIdUrl ) || !needsProviderIdUrl ){
            if( needsProviderIdUrl && ! checkValidIDUrl() ){
                setProviderUrlIdNotice(providerUrlIdNoticeList[selectedProvider])
               setTimeout( () => {
                    setProviderUrlIdNotice(null)
               }, 60000)
               return false;
            }
            setProviderUrlIdNotice(null)

            // Special handling for providers with dedicated AJAX endpoints
            let action = 'sbr_feed_saver_manager_add_source';
            let formData = {};

            if (selectedProvider === 'woocommerce') {
                // Check if using multi-selection (products, categories, or tags)
                if ( hasAnyWooSelection() ) {
                    action = 'sbr_add_woocommerce_source_multi';
                    // Build formData with arrays for PHP handling
                    formData = {
                        action: action,
                        source_name: sourceName || __( 'WooCommerce Products', 'sb-customizer' )
                    };
                    // Add product_ids as separate entries for PHP array handling
                    selectedProducts.forEach( ( product, index ) => {
                        formData[ 'product_ids[' + index + ']' ] = product.id;
                    });
                    // Add category_ids as separate entries for PHP array handling
                    selectedCategories.forEach( ( category, index ) => {
                        formData[ 'category_ids[' + index + ']' ] = category.id;
                    });
                    // Add tag_ids as separate entries for PHP array handling
                    selectedTags.forEach( ( tag, index ) => {
                        formData[ 'tag_ids[' + index + ']' ] = tag.id;
                    });
                } else {
                    // Fallback to single product (URL mode)
                    action = 'sbr_add_woocommerce_source';
                    formData = {
                        action: action,
                        product_id: providerIdUrl // Can be ID or URL
                    };
                }
            } else if (selectedProvider === 'edd') {
                // Check if using multi-selection (downloads, categories, or tags)
                if ( hasAnyEddSelection() ) {
                    action = 'sbr_add_edd_source_multi';
                    // Build formData with arrays for PHP handling
                    formData = {
                        action: action,
                        source_name: eddSourceName || __( 'EDD Downloads', 'sb-customizer' )
                    };
                    // Add download_ids as separate entries for PHP array handling
                    selectedDownloads.forEach( ( download, index ) => {
                        formData[ 'download_ids[' + index + ']' ] = download.id;
                    });
                    // Add category_ids as separate entries for PHP array handling
                    selectedEddCategories.forEach( ( category, index ) => {
                        formData[ 'category_ids[' + index + ']' ] = category.id;
                    });
                    // Add tag_ids as separate entries for PHP array handling
                    selectedEddTags.forEach( ( tag, index ) => {
                        formData[ 'tag_ids[' + index + ']' ] = tag.id;
                    });
                } else {
                    // Fallback to single download (URL mode)
                    action = 'sbr_add_edd_source';
                    formData = {
                        action: action,
                        download_id: providerIdUrl // Can be ID or URL
                    };
                }
            } else if (selectedProvider === 'airbnb') {
                action = 'sbr_add_airbnb_source';
                formData = {
                    action: action,
                    listing_url: providerIdUrl // Can be listing URL or ID
                };
            } else if (selectedProvider === 'booking') {
                action = 'sbr_add_booking_source';
                formData = {
                    action: action,
                    hotel_url: providerIdUrl // Can be hotel URL or ID
                };
            } else if (selectedProvider === 'aliexpress') {
                action = 'sbr_add_aliexpress_source';
                formData = {
                    action: action,
                    product_url: providerIdUrl // Can be product URL or ID
                };
            } else {
                // Existing logic for other providers
                formData = {
                    action: action,
                    provider: selectedProvider,
                    apiKey: SbUtils.checkNotEmpty( apiKey ) !== false ? apiKey : null,
                    providerIdUrl: providerIdUrl
                };
            }

            // Add nonce for security verification
            formData.nonce = sbCustomizer?.nonce;

            SbUtils.ajaxPost(
                sbCustomizer.ajaxHandler,
                formData,
                ( data ) => {
                    editorTopLoader.setLoader( false );

                    if( data?.error !== undefined ){
                        let errorMessage = data?.message || __('Unknown error occurred!', 'sb-customizer');
                        if( data?.error ===  'sourceLimitExceeded'){
                            let isProviderNeedAPI = ! ['trustpilot', 'wordpress.org'].includes(selectedProvider);
                            errorMessage = isProviderNeedAPI
                            ? __('Source Limit Exceeded. Please enter API Key.', 'sb-customizer')
                            : __('Source Limit Exceeded.', 'sb-customizer');

                            setAPIKeyNotice( 'sourceLimitExceeded' );
                            setTimeout(() => {
                                setCurrentStep( isProviderNeedAPI ? 'addApiKey' : 'selectProvider' )
                            }, 1000);
                        }

                        const errorUnknown = {
                            type : 'error',
                            icon : 'notice',
                            text : errorMessage
                        }
                        SbUtils.applyNotification( errorUnknown , editorNotification )
                    }else{
                        const duration = data?.apikey !== undefined ? 500 : 0;
                        processAPIKeyReturn( data?.apikey );
                        setTimeout(() => {
                            processLocationReturn( data?.placeId, data?.message, data?.notice );
                        }, duration);

                        if( data?.apiKeys !== undefined ){
                            apis.setApiKeys( data?.apiKeys );
                        }
                        // Don't update sources list from response - parent will refresh for current page
                        // This prevents showing page 1 results while staying on current pagination page
                        if( data?.sourcesCount !== undefined ){
                            sourcesCount.setSourcesNumber( data?.sourcesCount );
                        }
                        if( data?.newAddedSource !== undefined ){
                            localStorage.setItem('newAddedSourceId', data?.newAddedSource?.id)
                        }
                        // Notify parent that a source was successfully added
                        if ( props.onSuccess ) {
                            props.onSuccess( data?.newAddedSource );
                        }
                        if ( data?.bulkStarted !== undefined){
                            const errorUnknown = {
                                type : 'success',
                                icon : 'success',
                                text : __('Fetching reviews history in the background!', 'reviews-feed'),
                                time : 7000
                            }
                             setTimeout(() => {
                                SbUtils.applyNotification( errorUnknown , editorNotification )
                            }, 3200);
                        }
                    }
                    if( data?.apiKeyLimits !== undefined ){
                        const newApiLimits = [...data?.apiKeyLimits]
                        apiLimits.setApiKeyLimits( newApiLimits );
                    }
                    if( data?.pluginNotices !== undefined
                        && data?.pluginNotices.length > 0
                    ){
                        const newPluginNotices = [...data?.pluginNotices]
                        globalNotices.setPluginNotices( newPluginNotices );
                    }
                    if (data?.freeRetrieverData) {
                        freeRet.setFreeRetrieverData(data?.freeRetrieverData)
                    }
                },
                editorTopLoader,
                editorNotification,
                null
            )
        }
    }

    const setReturnError = ( errorType ) => {
        setInputError( errorType );
        setTimeout(() => {
            setInputError( null );
        }, 3500);
    }

    const processAPIKeyReturn = ( apiKeyReturn ) => {
        if( apiKeyReturn !== undefined ){
            let notificationApiKey = {};
            if( apiKeyReturn === 'valid' ){
                notificationApiKey = {
                    icon : 'success',
                    text : __('API Key Added', 'sb-customizer')
                }
            }
            else if( apiKeyReturn === 'invalid' ){
                setCurrentStep( 'addApiKey' );
                setReturnError( 'errorAPIKey' );
                notificationApiKey = {
                    type : 'error',
                    icon : 'notice',
                    text : __('Invalid API Key', 'sb-customizer')
                }
            }
            SbUtils.applyNotification( notificationApiKey , editorNotification )
        }

    }

    const processLocationReturn = ( placeId, message, notice = null ) => {
        let notificationPlaceId = {};
        if( placeId !== undefined ){
            if( placeId === 'invalid' ){
                setCurrentStep( 'addSourceDetails' );
                setReturnError( 'errorLocation' );
                notificationPlaceId = {
                    type : 'error',
                    icon : 'notice',
                    text : __('Invalid Location ID or URL', 'sb-customizer'),
                    time : 15000
                }
            }
        }
        if( message !== undefined  && message === 'addedSource') {
            if (
                freeRet?.freeRetrieverData?.providers.includes(selectedProvider)
                && !SbUtils.checkAPIKeyExists(selectedProvider, apis)
            ) {
                applyRetrievalResponseNotice()
            } else {
                cancelClick();
            }
            notificationPlaceId = {
                icon : 'success',
                text : __('Source added successfully', 'sb-customizer')
            }
        }
        // Handle existing source case - show success notification and close modal
        if( message !== undefined && message === 'sourceExists') {
            cancelClick();
            notificationPlaceId = {
                icon : 'success',
                text : notice || __('This source already exists', 'sb-customizer'),
                time : 6000
            }
        }


        SbUtils.applyNotification( notificationPlaceId , editorNotification )
    }

    const checkFreeRetrievalStep = (provider) => {
        if (SbUtils.checkAPIKeyExists(provider, apis)) {
            setCurrentStep('addSourceDetails')
        } else {
            const shouldLimit               = SbUtils.shouldLimiFreeRetrieval(provider, apis, freeRet);
            const shouldLimitFreeUserEmail  = SbUtils.shouldLimiFreeUserEmail(provider, apis, freeRet);
            if (shouldLimit === true) {
                addSModal.setModalType('freeRetriever')
            } else if(shouldLimitFreeUserEmail) {
                freeRetModal.setRetModalType("verifyEmail")
                addSModal.setModalType('freeRetriever')
            }
            else {
                addSModal.setModalType('addSource')
                setCurrentStep('addSourceDetails')
            }
        }
    }

    const applyRetrievalResponseNotice = () => {
        addSModal.setModalType('freeRetriever')
        freeRetModal?.setRetModalType('sourceAdded')
    }

    const nextStepClick = () => {
        switch (currentStep) {
            case 'selectProvider':
                // WooCommerce has its own flow: selectProvider -> addProducts -> addSourceName
                if ( selectedProvider === 'woocommerce' ) {
                    // Set loading states BEFORE changing step to prevent Notice flash
                    setLoadingProducts( true );
                    setLoadingCategories( true );
                    setLoadingTags( true );
                    setCurrentStep( 'addProducts' );
                    return;
                }

                // EDD has its own flow: selectProvider -> addDownloads -> addEddSourceName
                if ( selectedProvider === 'edd' ) {
                    // Set loading states BEFORE changing step to prevent Notice flash
                    setLoadingDownloads( true );
                    setLoadingEddCategories( true );
                    setLoadingEddTags( true );
                    setCurrentStep( 'addDownloads' );
                    return;
                }

                if (!freeRet?.freeRetrieverData?.providers.includes(selectedProvider)) {
                    // Skip API key step if: already has key, provider doesn't need key, or provider uses backend-managed keys
                    const hasBackendManagedKey = selectedProviderInfo?.apiKeyType !== undefined;
                    const skipApiKey = apiKeys[selectedProvider] !== undefined || providersWithNoApiKey.includes(selectedProvider) || hasBackendManagedKey;
                    setCurrentStep(skipApiKey ? 'addSourceDetails' : 'addApiKey')
                } else {
                    checkFreeRetrievalStep(selectedProvider)
                }
            break;
            case 'addApiKey':
                if (selectedProvider !== 'google') {
                    setCurrentStep('addSourceDetails');
                }
            break;
            case 'addProducts':
                // WooCommerce: validate selection before moving to name step
                const wooValidationMsg = validateWooSelection();
                if ( wooValidationMsg ) {
                    setWooValidationError( wooValidationMsg );
                    return;
                }
                setWooValidationError( null );
                setCurrentStep( 'addSourceName' );
            break;
            case 'addSourceName':
                // WooCommerce: finish and create the source
                finishStepClick();
            break;
            case 'addDownloads':
                // EDD: validate selection before moving to name step
                const eddValidationMsg = validateEddSelection();
                if ( eddValidationMsg ) {
                    setEddValidationError( eddValidationMsg );
                    return;
                }
                setEddValidationError( null );
                setCurrentStep( 'addEddSourceName' );
            break;
            case 'addEddSourceName':
                // EDD: finish and create the source
                finishStepClick();
            break;
            case 'addSourceDetails' :
                finishStepClick()
            break;
            default:
                setCurrentStep('selectProvider')
            break;
        }
    }

    const connectWithFacebookAction = () => {
        window.fbConnectProcess = true;
        const appendURL = sbCustomizer.isFeedEditor ?
                        sbCustomizer.connectFBUrls.stateURL + ',feed_id=' + sbCustomizer.feedData.feed_info.id : sbCustomizer.connectFBUrls.stateURL;
        const params = sbCustomizer.connectFBUrls.page;
        const ifConnectURL = params.connect;
        const urlParams = {
            'wordpress_user' : params.wordpress_user,
            'v' : params.v,
            'vn' : params.vn,
            'cff_con' : params.cff_con,
            'state' : "{'{url=" + appendURL + "}'}"
        };
        if(params.sw_feed) {
            urlParams['sw-feed'] = 'true';
        }
        let form = document.createElement('form');
            form.method = 'POST';
            form.action = ifConnectURL;

            for (const param in urlParams) {
                if (urlParams.hasOwnProperty(param)) {
                    let input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = param;
                    input.value = urlParams[param];
                    form.appendChild(input);
                }
            }

            document.body.appendChild(form);
            form.submit();
    }


    const getTooltipText = ( provider ) => {
        const tiersListText = (tiersInfo !== undefined && tiersInfo[provider.type] !== undefined && tiersInfo[provider.type] === 3) ? __('"Elite"', 'sb-customizer' ) : __('"Plus" and "Elite"', 'sb-customizer' );

        return `<p>
                    ${provider.name}  ${__( 'reviews are available for the', 'sb-customizer') +' '+ tiersListText +' '+  __('license tiers only, please upgrade your license on ', 'sb-customizer' ) }
                <a
                    href="https://smashballoon.com/extensions/reviews-feed-pro/?reviews&utm_campaign=reviews-pro&utm_source=feedcreate&utm_medium=connect&utm_content=upgrade"
                    rel="noreferrer"
                    target="_blank"
                > SmashBalloon </a>
                ${__( 'in order to connect a ', 'sb-customizer' ) +  provider.name  + ' ' + __( 'source.', 'sb-customizer' ) }
            </p>`

    }

    const cancelClick = () => {
        props.onCancel();
    }

    useEffect(() => {
        if (
            props?.onSuccessApiKey !== undefined
            && props?.onSuccessApiKey !== null
        ) {
            setSelectedProvider(props?.onSuccessApiKey)
            setCurrentStep('addSourceDetails')
        }
    }, [props?.onSuccessApiKey])

    let navInd = 0;
    return (
        <div className="sb-addsource-modal sb-fs" data-pro={isPro}>
            <div className="sb-addsource-modal-heading sb-fs">
                <h4 className='sb-h4'>{ __( 'Add Sources', 'sb-customizer' ) }</h4>
            </div>
            <div className='sb-addsource-modal-navtop sb-fs'>
                {
                   addSourcesSteps.map( ( step, index ) => {
                        const showNavItem = step?.condition === undefined || step.condition;
                        navInd = showNavItem ? navInd + 1 : navInd;
                        return (
                            ( showNavItem ) &&
                            <div
                                className='sb-addsource-modal-navitem'
                                data-active={currentStep === step.id}
                                key={ index }
                                onClick={ () => {
                                    navTopClick( step.id )
                                } }
                            >
                                <span className='sb-addsource-modal-navitem-num'>{ navInd }</span>
                                <span className={ (currentStep === step.id ? 'sb-dark-text ' : 'sb-dark2-text ' )+ 'sb-text-tiny sb-bold'}>{ step.heading }</span>
                            </div>
                        )
                   } )
                }
            </div>
            {
                currentStep === 'selectProvider' &&
                <>
                    <div className='sb-addsource-modal-providers-list sb-fs' data-type="default">
                        {
                            providers.map( provider => {
                                const isLicenseDisabled = ! allowedTiers.tiers.includes( provider.type );
                                const isPluginDisabled = provider.pluginRequired === true;
                                const isDisabled = isLicenseDisabled || isPluginDisabled;

                                return (
                                    <div
                                        className='sb-addsource-modal-provider-item sb-fs'
                                        key={ provider.type }
                                        data-disabled={ isLicenseDisabled }
                                        data-plugin-disabled={ isPluginDisabled }
                                        onClick={ () => {
                                            if( ! isDisabled ){
                                                const sProvider = provider.type === selectedProvider ? null : provider.type;
                                                setSelectedProvider( sProvider )
                                                // Clear URL input when switching providers to prevent stale data
                                                setProviderIdUrl( '' );
                                                setInputError( null );
                                                if(sProvider !== 'google'){
                                                    setAPIKeyNotice( apiLimits?.apiKeyLimits.includes( sProvider ) ? 'sourceLimitExceeded' : 'default' );
                                                }
                                            }
                                        }}
                                        data-active={ provider.type === selectedProvider}
                                    >
                                        {
                                            isLicenseDisabled &&
                                            SbUtils.printTooltip( getTooltipText( provider ) , {
                                                type : 'white',
                                                textAlign : 'center',
                                                width : 'full',
                                                replaceText : false
                                            } )
                                        }
                                        {
                                            isPluginDisabled &&
                                            <div className='sb-provider-plugin-tooltip'>
                                                { provider.pluginRequiredMessage }
                                            </div>
                                        }
                                        { SbUtils.printIcon( provider.type + '-provider' ) }
                                        <span className='sb-text-small'>{ provider.name }</span>
                                        {
                                            isPluginDisabled &&
                                            SbUtils.printIcon( 'plugin-disabled-warning', '', false, 14 )
                                        }
                                    </div>
                                )
                            } )
                        }
                    </div>
                    {
                        !isPro &&
                        <>
                            <span className='sb-addsource-advanced-hd sb-fs sb-bold sb-text-tiny sb-light-text'>{__('ADVANCED SOURCES', 'sb-customizer')}</span>
                            <div className='sb-addsource-modal-providers-list sb-fs' data-type="advanced">
                                {
                                    advancedProviders.map( provider => {
                                        return (
                                            <div
                                                className='sb-addsource-modal-provider-item sb-fs'
                                                key={ provider.type }
                                                onClick={ () => {
                                                    upsellModal.setUpsellActive( provider.type + 'Provider')
                                                }}
                                            >
                                                { SbUtils.printIcon( provider.type + '-provider' ) }
                                                <span className='sb-text-small sb-addsource-advanced-provider-name'>
                                                    <span>{ provider.name }</span>
                                                    { SbUtils.printIcon( 'rocket', 'sb-addsource-advanced-icon', false, 12 ) }
                                                </span>
                                            </div>
                                        )
                                    } )
                                }
                            </div>
                        </>
                    }

                    <div className='sb-collection-srmodal-ctn sb-fs'>
                        <div className='sb-collection-srmodal-ins sb-fs'>
                            {SbUtils.printIcon('collection-provider', '', false, 32)}
                            <div className='sb-collection-srmodal-text'>
                                <strong>{ __( 'Handpick reviews with collections', 'reviews-feed' ) }</strong>
                                <p>{ __( 'Select among your reviews and organize them into a collection. Manually create a review for complete control. Once a collection is created, it will appear as a feed source.', 'reviews-feed' ) }</p>
                            </div>
                            <Button
                                type='secondary'
                                size='small'
                                icon='plus'
                                boxshadow='false'
                                iconSize='10'
                                text={ __( 'Create Collection', 'sb-customizer' ) }
                                link={sbCustomizer?.collectionsPageUrl}
                            />
                        </div>
                    </div>

                    {
                        ( apiLimits.apiKeyLimits.includes('wordpress.org') || apiLimits.apiKeyLimits.includes('trustpilot') ) &&
                        <div className='sb-reached-notice-limit-ctn sb-fs'>
                            <Notice
                                type='error'
                                text={
                                    __( 'You have reached the allowed the number of sources for ', 'sb-customizer') +
                                    (apiLimits.apiKeyLimits.includes('wordpress.org') ? 'WordPress.Org' : '') +
                                    ((apiLimits.apiKeyLimits.includes('wordpress.org') && apiLimits.apiKeyLimits.includes('trustpilot') ) ? ' & ' : '') +
                                    (apiLimits.apiKeyLimits.includes('trustpilot') ? 'Trustpilot' : '') +
                                    '.'
                                }
                            />
                        </div>
                    }
                </>
            }

            {
                currentStep === 'addApiKey' && !['trustpilot', 'wordpress.org'].includes(selectedProvider) &&
                <div className='sb-addsource-modal-apikey-ctn sb-fs'>
                    <div className='sb-addsource-modal-apikey-heading sb-fs'>
                        <span className='sb-standard-p sb-bold '>
                            { __( 'Add an API Key for ', 'sb-customizer' ) + selectedProviderInfo?.name }
                        </span>
                        <p className='sb-small-p sb-fs'>
                            {
                                selectedProviderInfo?.type !== 'tripadvisor' &&
                                __( 'API key gives us permission to fetch data from ', 'sb-customizer' )
                                + selectedProviderInfo?.name +
                                __( '. You only need to add this once. For any future ', 'sb-customizer' )
                                + selectedProviderInfo?.name +
                                __( ' accounts, we will use the same API key.', 'sb-customizer' )
                            }
                            {
                                selectedProviderInfo?.type === 'tripadvisor' &&
                                <span>
                                    { __( 'The TripAdvisor API has a significant monthly cost. Your feeds will still update without entering an API key but you can choose to add your own.', 'sb-customizer' ) }<br/>
                                </span>
                            }
                            {
                                selectedProviderInfo?.docLink &&
                                <>
                                    <br/>
                                    <a
                                        href={selectedProviderInfo?.docLink}
                                        target='_blank'
                                        rel='noreferrer'
                                    >
                                        { __( 'How to Create an API key.', 'sb-customizer' ) }<br/>
                                    </a>
                                </>
                            }
                        </p>
                    </div>
                    <div
                        className='sb-addsource-modal-apikey-input sb-fs'
                        data-error={ inputError === 'errorAPIKey' }
                    >
                        <Input
                            size='large'
                            placeholder={ __( 'Add API Key', 'sb-customizer' ) }
                            leadingIcon='key'
                            disablebg='true'
                            value={ apiKey }
                            onChange = { ( event ) =>
                                setApiKey( event.currentTarget.value )
                            }
                        />
                    </div>
                    {
                        selectedProvider !== 'google' && selectedProvider !== 'tripadvisor' &&
                        <Notice
                            type={ apiKeyNoticeInfo?.type }
                            text={ apiKeyNoticeInfo?.text }
                        />
                    }
                </div>
            }
            {
                currentStep === 'addSourceDetails' && selectedProvider !== 'facebook' && selectedProvider !== 'woocommerce' && selectedProvider !== 'edd' && ! connectFacebookActiveMemo &&
                <div className='sb-addsource-modal-apikey-ctn sb-fs' data-type='addsourcedetails'>
                    <div className='sb-addsource-modal-apikey-heading sb-fs'>
                        <span className='sb-standard-p sb-bold '>{ selectedProviderInfo?.heading }</span>
                        <p className='sb-small-p sb-fs'>
                            {__( 'This helps us identify which page to fetch reviews from', 'sb-customizer' )}
                            {
                                selectedProvider === 'google' &&
                                <span>
                                    <br/>
                                    {__( 'Find your source\'s', 'sb-customizer' )} <a href='https://developers.google.com/maps/documentation/places/web-service/place-id' target='blank'> {__( 'place ID here', 'sb-customizer' )}</a>
                                </span>
                            }
                        </p>
                    </div>
                    <div
                        className='sb-addsource-modal-apikey-input sb-fs'
                        data-error={ inputError === 'errorLocation' }
                    >
                        <Input
                            size='large'
                            placeholder={ selectedProviderInfo?.placeholder }
                            leadingIcon='globe'
                            disablebg='true'
                            value={ providerIdUrl }
                            onChange = { ( event ) =>
                                setProviderIdUrl( event.currentTarget.value )
                            }
                        />
                    </div>
                    {
                        providerUrlIdNotice !== null &&
                        <Notice
                            type="default"
                            text={ providerUrlIdNotice }
                        />
                    }
                </div>
            }
            {
                currentStep === 'addSourceDetails' && selectedProvider === 'facebook' && connectFacebookActiveMemo &&
                <div className='sb-addsource-modal-apikey-ctn sb-fs' data-type='addsourcedetails'>
                    <div className='sb-fbconnect-top sb-text-tiny sb-dark2-text sb-fs'>
                        { __( 'Showing', 'sb-customizer' ) } <strong className='sb-dark-text'>{ __( 'Facebook Pages', 'sb-customizer' ) }</strong> { __( 'connected to', 'sb-customizer' ) }
                        <img src={sbCustomizer?.newSourceData?.user?.picture?.data?.url } alt='Profile' /> <strong className='sb-dark-text'>{sbCustomizer?.newSourceData?.user?.name }</strong>
                    </div>
                    <div className='sb-fbconnect-list sb-fs'>
                        {
                            sbCustomizer?.newSourceData?.pages.map( page => {
                                return (
                                    <div
                                        className='sb-fbconnect-singlepage'
                                        data-active={ selectedFBPages.includes( page.id ) === true  }
                                        key={ page.id }
                                        onClick = { () =>
                                            selectFBPages( page.id )
                                        }
                                    >
                                        <Checkbox
                                            value={ selectedFBPages.includes( page.id ) }
                                            enabled={ true }
                                            onChange = { () =>
                                                selectFBPages( page.id )
                                            }
                                        />
                                        <img src={'https://graph.facebook.com/' + page.id + '/picture'} alt={page?.name}/>
                                        <strong className='sb-text-small sb-bold'>
                                            { page?.name }
                                        </strong>
                                    </div>
                                )
                            } )
                        }
                    </div>
                </div>

            }
            {
                currentStep === 'addProducts' && selectedProvider === 'woocommerce' && ! connectFacebookActiveMemo &&
                <div className='sb-addsource-modal-apikey-ctn sb-fs sb-woo-multi-product' data-type='woocommerce'>

                    {/* Shared Product Selector Component with Tabs */}
                    {
                        ( wooProducts.length > 0 || wooCategories.length > 0 || wooTags.length > 0 || loadingProducts || loadingCategories || loadingTags ) ? (
                            <WooProductSelector
                                showTabs={ true }
                                products={ wooProducts }
                                selectedProducts={ selectedProducts }
                                onProductToggle={ toggleProductSelection }
                                onSelectAll={ selectAllProducts }
                                onDeselectAll={ deselectAllProducts }
                                isLoading={ loadingProducts }
                                validationError={ wooValidationError }
                                categories={ wooCategories }
                                selectedCategories={ selectedCategories }
                                onCategoryToggle={ toggleCategorySelection }
                                onSelectAllCategories={ selectAllCategories }
                                onDeselectAllCategories={ deselectAllCategories }
                                loadingCategories={ loadingCategories }
                                tags={ wooTags }
                                selectedTags={ selectedTags }
                                onTagToggle={ toggleTagSelection }
                                onSelectAllTags={ selectAllTags }
                                onDeselectAllTags={ deselectAllTags }
                                loadingTags={ loadingTags }
                                onClearAll={ clearAllSelections }
                                onSearchProducts={ loadWooCommerceProducts }
                                isSearching={ isSearchingProducts }
                            />
                        ) : (
                            <Notice
                                type='default'
                                text={ __( 'No products found in your WooCommerce store.', 'sb-customizer' ) }
                            />
                        )
                    }
                </div>
            }
            {
                currentStep === 'addSourceName' && selectedProvider === 'woocommerce' && ! connectFacebookActiveMemo &&
                <div className='sb-woo-source-name-step'>
                    <div className='sb-woo-source-name-heading'>
                        <span className='sb-woo-source-name-title'>
                            { __( 'Name your source', 'sb-customizer' ) }
                        </span>
                        <p className='sb-woo-source-name-desc'>
                            { __( 'This will show up in sources panel to make this easy to identify', 'sb-customizer' ) }
                        </p>
                    </div>

                    <div className='sb-woo-source-name-input'>
                        <input
                            type='text'
                            className='sb-woo-source-name-field'
                            placeholder={ __( 'Eg. "Electronic Products" or "All products"', 'sb-customizer' ) }
                            value={ sourceName }
                            onChange={ (event) => setSourceName( event.currentTarget.value ) }
                        />
                    </div>
                </div>
            }
            {
                currentStep === 'addDownloads' && selectedProvider === 'edd' && ! connectFacebookActiveMemo &&
                <div className='sb-addsource-modal-apikey-ctn sb-fs sb-woo-multi-product' data-type='edd'>

                    {/* Shared Download Selector Component with Tabs */}
                    {
                        ( eddDownloads.length > 0 || eddCategories.length > 0 || eddTags.length > 0 || loadingDownloads || loadingEddCategories || loadingEddTags ) ? (
                            <EddDownloadSelector
                                showTabs={ true }
                                downloads={ eddDownloads }
                                selectedDownloads={ selectedDownloads }
                                onDownloadToggle={ toggleDownloadSelection }
                                onSelectAll={ selectAllDownloads }
                                onDeselectAll={ deselectAllDownloads }
                                isLoading={ loadingDownloads }
                                validationError={ eddValidationError }
                                categories={ eddCategories }
                                selectedCategories={ selectedEddCategories }
                                onCategoryToggle={ toggleEddCategorySelection }
                                onSelectAllCategories={ selectAllEddCategories }
                                onDeselectAllCategories={ deselectAllEddCategories }
                                loadingCategories={ loadingEddCategories }
                                tags={ eddTags }
                                selectedTags={ selectedEddTags }
                                onTagToggle={ toggleEddTagSelection }
                                onSelectAllTags={ selectAllEddTags }
                                onDeselectAllTags={ deselectAllEddTags }
                                loadingTags={ loadingEddTags }
                                onClearAll={ clearAllEddSelections }
                                onSearchDownloads={ loadEddDownloads }
                                isSearching={ isSearchingDownloads }
                            />
                        ) : (
                            <Notice
                                type='default'
                                text={ __( 'No downloads found in your EDD store.', 'sb-customizer' ) }
                            />
                        )
                    }
                </div>
            }
            {
                currentStep === 'addEddSourceName' && selectedProvider === 'edd' && ! connectFacebookActiveMemo &&
                <div className='sb-woo-source-name-step'>
                    <div className='sb-woo-source-name-heading'>
                        <span className='sb-woo-source-name-title'>
                            { __( 'Name your source', 'sb-customizer' ) }
                        </span>
                        <p className='sb-woo-source-name-desc'>
                            { __( 'This will show up in sources panel to make this easy to identify', 'sb-customizer' ) }
                        </p>
                    </div>

                    <div className='sb-woo-source-name-input'>
                        <input
                            type='text'
                            className='sb-woo-source-name-field'
                            placeholder={ __( 'Eg. "Premium Plugins" or "All downloads"', 'sb-customizer' ) }
                            value={ eddSourceName }
                            onChange={ (event) => setEddSourceName( event.currentTarget.value ) }
                        />
                    </div>
                </div>
            }

            <div
                className='sb-addsource-modal-provider-btn sb-fs'
            >
                {/* WooCommerce-specific buttons */}
                {
                    selectedProvider === 'woocommerce' && (
                        <>
                            <Button
                                type='secondary'
                                size='medium'
                                text={ __( 'Cancel', 'sb-customizer' ) }
                                onClick={ cancelClick }
                            />
                            <Button
                                type='primary'
                                size='medium'
                                icon='chevron-right'
                                icon-position='right'
                                iconSize='8'
                                text={
                                    currentStep === 'addSourceName'
                                    ? __( 'Finish', 'sb-customizer' )
                                    : __( 'Next', 'sb-customizer' )
                                }
                                onClick={ nextStepClick }
                            />
                        </>
                    )
                }
                {/* EDD-specific buttons */}
                {
                    selectedProvider === 'edd' && (
                        <>
                            <Button
                                type='secondary'
                                size='medium'
                                text={ __( 'Cancel', 'sb-customizer' ) }
                                onClick={ cancelClick }
                            />
                            <Button
                                type='primary'
                                size='medium'
                                icon='chevron-right'
                                icon-position='right'
                                iconSize='8'
                                text={
                                    currentStep === 'addEddSourceName'
                                    ? __( 'Finish', 'sb-customizer' )
                                    : __( 'Next', 'sb-customizer' )
                                }
                                onClick={ nextStepClick }
                            />
                        </>
                    )
                }
                {/* Non-WooCommerce/EDD buttons */}
                {
                    selectedProvider !== 'woocommerce' && selectedProvider !== 'edd' && selectedProvider !== 'facebook' && (
                        (
                            (
                                ! apiLimits?.apiKeyLimits.includes( selectedProvider ) || currentStep !== 'addApiKey' )
                                && ((mandatoryApiKeyProviders.includes(selectedProvider) && currentStep !== 'addApiKey' ) || !mandatoryApiKeyProviders.includes(selectedProvider))
                            ) &&
                        <Button
                            type='secondary'
                            size='medium'
                            text={ currentStep === 'addApiKey' ? __( 'Skip', 'sb-customizer' ) : __( 'Cancel', 'sb-customizer' ) }
                            onClick={ () => {
                            if( currentStep === 'addApiKey' ){
                                nextStepClick();
                            }else{
                                cancelClick()
                            }
                            } }
                        />
                    )
                }
                {
                    //Check if we need to display the Next Button or Not (for non-WooCommerce/EDD providers)
                    selectedProvider !== 'woocommerce' && selectedProvider !== 'edd' &&
                    (
                        (
                            needsApiKey &&
                            (
                                ( !apiLimits?.apiKeyLimits.includes( selectedProvider ) && !mandatoryApiKeyProviders.includes(selectedProvider) ) ||
                                currentStep !== 'addApiKey' ||
                                (SbUtils.checkNotEmpty( apiKey ) && currentStep === 'addApiKey')
                            )
                        )
                        || providersWithNoApiKey.includes(selectedProvider) //Providers with No API Kedy
                        || freeRet?.freeRetrieverData?.providers.includes(selectedProvider) //V2 Yelp & Google No API Key
                    )
                    &&
                    <Button
                        type='primary'
                        size='medium'
                        icon='chevron-right'
                        icon-position='right'
                        iconSize='8'
                        text={
                            currentStep === 'addSourceDetails'
                            ? __( 'Finish', 'sb-customizer' )
                            : __( 'Next', 'sb-customizer' )
                        }
                        onClick={ () => {
                            nextStepClick()
                        } }
                    />
                }
                {
                    selectedProvider === 'facebook' && ! connectFacebookActiveMemo &&
                    <Button
                        type='primary'
                        size='medium'
                        icon='facebook'
                        iconSize='22'
                        text={ __( 'Connect with Facebook', 'sb-customizer' ) }
                        onClick={ () => {
                            connectWithFacebookAction()
                        } }
                    />
                }
                {
                    selectedProvider === 'facebook' && connectFacebookActiveMemo &&
                    <Button
                        type='primary'
                        size='medium'
                        icon='plus'
                        iconSize='12'
                        text={ __( 'Add Pages as Source', 'sb-customizer' ) }
                        onClick={ () => {
                            addFacebookPagesSource()
                        } }
                    />
                }
            </div>
        </div>
    )
}

export default AddSourceModal;